'use client';

import { useCallback, useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import { MapPinLineIcon } from '@/components/icons';
import { cn } from '@/lib/utils';
import { useAddressStore } from '@/stores/address-store';

/**
 * Окно «нужен адрес».
 *
 * Раньше на этом месте был тост: он жил 4 секунды, не объяснял причину и
 * исчезал раньше, чем человек успевал прочитать. Карта при этом открывалась
 * сама, и получалось, что тебя без объяснений бросили на полноэкранную карту.
 *
 * Теперь сначала короткое объяснение с одной кнопкой, а карта открывается
 * только по ней.
 */
export function AddressRequiredDialog() {
  const source = useAddressStore((s) => s.requiredPromptSource);
  const closePrompt = useAddressStore((s) => s.closeRequiredPrompt);
  const openSheet = useAddressStore((s) => s.openSheet);
  const reduceMotion = useReducedMotion();

  const isOpen = source !== null;
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  const handleContinue = useCallback(() => {
    // openSheet сам гасит это окно — состояние в одном месте, без гонки.
    openSheet('checkout');
  }, [openSheet]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePrompt();
    };
    document.addEventListener('keydown', onKeyDown);

    // Фокус на действии: с клавиатуры сразу можно продолжить.
    const focusTimer = window.setTimeout(() => confirmRef.current?.focus(), 60);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [isOpen, closePrompt]);

  // Затухание — линейное и быстрое, появление — ease-out-expo без пружин.
  const panelMotion = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, scale: 0.96, y: 10 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.98, y: 4 },
      };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[9600] flex items-end justify-center sm:items-center sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="address-required-title"
        >
          <motion.div
            className="absolute inset-0 bg-[color-mix(in_oklab,var(--text-primary)_55%,transparent)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'linear' }}
            onClick={closePrompt}
          />

          <motion.div
            {...panelMotion}
            transition={{ duration: reduceMotion ? 0.15 : 0.32, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'relative w-full sm:w-[420px]',
              'bg-[var(--surface-primary)]',
              // На мобильном это шторка снизу → screen(32); от sm это модалка
              // по центру → card(20). Ступень выбирается по тому, чем элемент
              // является на этом брейкпоинте.
              'rounded-t-screen sm:rounded-card',
              'border-t border-[var(--border-default)] sm:border',
              // Модалка — одно из двух исключений, которым объём оставлен
              // (ответ 35, второй вопрос = 2).
              'shadow-deep',
              'px-6 pt-7 pb-6 sm:px-7 sm:pt-8 sm:pb-7'
            )}
          >
            <div
              className={cn(
                // Декоративная иконка-плашка → pill, как все остальные плашки
                // зоны (w-8/w-9/w-10/w-16 в auth-modal и address-sheet).
                // Была единственной, оставшейся квадратной со скруглением.
                'w-12 h-12 rounded-pill flex items-center justify-center',
                'bg-[var(--primary-subtle)] text-[var(--primary)]'
              )}
            >
              <MapPinLineIcon className="w-6 h-6" weight="bold" />
            </div>

            <h2
              id="address-required-title"
              className="mt-5 t-section-title text-[var(--text-primary)]"
            >
              Куда везём?
            </h2>

            <p className="mt-2.5 t-body text-[var(--text-secondary)]">
              По адресу мы посчитаем доставку и покажем итоговую сумму заказа. Это займёт
              несколько секунд.
            </p>

            <button
              ref={confirmRef}
              type="button"
              onClick={handleContinue}
              className={cn(
                'group mt-7 w-full py-4 rounded-card',
                'flex items-center justify-center gap-2',
                'bg-[var(--primary)] text-[var(--text-inverse)]',
                'text-[15px] font-semibold',
                'transition-[transform,filter] duration-200 ease-out',
                'hover:brightness-110 active:scale-[0.985]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-primary)]'
              )}
            >
              Указать адрес
              <ArrowRight
                className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
                weight="bold"
              />
            </button>

            <button
              type="button"
              onClick={closePrompt}
              className={cn(
                'mt-2 w-full py-3 rounded-card',
                'text-[14px] font-medium text-[var(--text-tertiary)]',
                'transition-colors duration-200 ease-out hover:text-[var(--text-secondary)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-default)]'
              )}
            >
              Позже
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
